import { Component, computed, effect, inject, input, OnInit } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FileModalComponent } from "@components/file-modal/file-modal.component";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { ProjectImageService } from "@domain/project/project-image.service";
import { TranslateProjectImageTagPipe } from "@domain/project/translate-project-image-tag.pipe";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { AutocompleteSearchFilterPipe } from "@odx/angular/components/autocomplete";
import { ModalService } from "@odx/angular/components/modal";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { map, Observable, scan, switchMap } from "rxjs";

@Component({
  selector: "app-image-notes",
  templateUrl: "./image-notes.component.html",
  styleUrls: ["./image-notes.component.scss"],
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    FloorplanItemDetailsComponent,
    TranslateProjectImageTagPipe,
    AutocompleteSearchFilterPipe,
  ],
  standalone: true,
})
export class ImageNotesComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private projectImageService = inject(ProjectImageService);
  private userService = inject(UserService);
  private floorplanService = inject(FloorplanService);

  readonly floorplanItem = input.required<FloorplanItem>();
  protected readonly floorplanImage = computed(() => this.floorplanItem() as FloorplanImage);
  protected readonly imageMetadata = toSignal(
    toObservable(this.floorplanImage).pipe(
      switchMap((image) => this.projectImageService.getImageMetadataFromIndexedDB(image.projectImageId)),
    ),
    { initialValue: null },
  );

  protected readonly previewUrl = toSignal(
    toObservable(this.floorplanImage).pipe(
      switchMap((image) => this.projectImageService.getPreviewImageFromIndexedDB(image.projectImageId)),
      map((blob) => (blob ? URL.createObjectURL(blob) : null)),
      scan(
        (previousUrl, currentUrl) => {
          if (previousUrl) URL.revokeObjectURL(previousUrl);
          return currentUrl;
        },
        null as string | null,
      ),
    ),
    { initialValue: null },
  );

  protected imageForm?: FormGroup;

  protected tagList$: Observable<string[]>;
  protected isMonitoringScope$: Observable<boolean>;

  constructor() {
    effect(() => {
      const image = this.imageMetadata();
      this.imageForm?.reset(
        { name: image?.name ?? "", tags: image?.tags ?? [], description: image?.description },
        { emitEvent: false },
      );
    });

    this.tagList$ = this.projectImageService.getProjectImageTags(BusinessScope.MONITORING);
    this.isMonitoringScope$ = this.userService.businessScope$.pipe(map((scope) => scope === BusinessScope.MONITORING));
  }

  ngOnInit() {
    this.initForm();
  }

  onShowImage() {
    this.modalService.open(FileModalComponent, {
      data: {
        imageMetadata: this.imageMetadata(),
        project: this.floorplanService.selectedFloorplan.project,
      },
    });
  }

  private update() {
    const metadata = this.imageMetadata();
    if (!metadata) return;
    metadata.name = this.imageForm!.value.name;
    metadata.tags = this.imageForm!.value.tags;
    metadata.description = this.imageForm!.value.description;
    this.projectImageService.updateImageInIndexedDB(metadata, this.floorplanService.selectedFloorplan.project).subscribe();
  }

  private initForm() {
    const metadata = this.imageMetadata();
    this.imageForm = this.formBuilder.group({
      name: [metadata?.name ?? "", Validators.required],
      tags: [metadata?.tags ?? []],
      description: [metadata?.description ?? ""],
    });

    this.imageForm.valueChanges.subscribe(() => {
      if (!this.imageForm!.valid || this.imageForm!.pristine) return;
      this.update();
    });
  }
}
